
import React, { useEffect } from 'react';
import { MapContainer, TileLayer, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Device, Consumer } from './types';
import DeviceMarker from './DeviceMarker';
import ConsumerMarker from './ConsumerMarker';
import { initializeLeafletIcons } from './utils';

interface MapCenterAdjusterProps {
  center: [number, number];
}

// Helper component to adjust map center
const MapCenterAdjuster: React.FC<MapCenterAdjusterProps> = ({ center }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center);
  }, [center, map]);
  
  return null;
};

interface GisMapViewProps {
  defaultCenter: [number, number];
  zoom: number;
  showDevices: boolean;
  showConsumers: boolean;
  filteredDevices: Device[];
  consumers: Consumer[];
  onSelectDevice?: (deviceId: string, deviceType: 'smartMeter' | 'gateway' | 'voltageRegulator', deviceData?: Device) => void;
}

const GisMapView: React.FC<GisMapViewProps> = ({
  defaultCenter,
  zoom,
  showDevices,
  showConsumers,
  filteredDevices,
  consumers,
  onSelectDevice
}) => {
  useEffect(() => {
    // Initialize Leaflet icons on component mount
    initializeLeafletIcons();
  }, []);

  // Ensure unique keys for each device to avoid React warning
  const getUniqueDeviceKey = (device: Device) => {
    return `${device.id}-${device.type}-${device.location.lat}-${device.location.lng}`;
  };

  return (
    <MapContainer 
      center={defaultCenter}
      style={{ height: '100%', width: '100%' }}
      zoom={zoom}
      zoomControl={false}
    >
      <MapCenterAdjuster center={defaultCenter} />
      <ZoomControl position="bottomright" />
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      
      {showDevices && filteredDevices.map(device => (
        <DeviceMarker 
          key={getUniqueDeviceKey(device)} 
          device={device} 
          onSelectDevice={onSelectDevice}
        />
      ))}
      
      {showConsumers && consumers.map(consumer => (
        <ConsumerMarker key={consumer.id} consumer={consumer} />
      ))}
    </MapContainer>
  );
};

export default GisMapView;
